import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  GraduationCap,
  BookOpen,
  Landmark,
  Beaker,
  Network,
  CalendarRange,
  ScrollText,
  Building2,
  Coins,
  FileSearch,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const TODAY_STATE = [
  {
    icon: Landmark,
    title: '11g and 12c installs against EBS or Fusion',
    detail:
      'Most higher-ed WebCenter AP installs are on 11g or 12c, posting to Oracle EBS Payables via the Payables Open Interface or to Fusion via FBDI. Many institutions started on 10g/11g during the 2010s capital cycle and rolled forward to 12c during the 2018-2021 window.',
  },
  {
    icon: Network,
    title: 'Procurement often runs on a separate system',
    detail:
      'Higher-ed procurement is frequently Jaggaer, Coupa, or SciQuest — separate from the AP layer. WebCenter handles invoice capture, coding, and approval routing; the procurement system handles requisitions, POs, and supplier onboarding. The matching boundary between the two is where most AP exception volume lives.',
  },
  {
    icon: ScrollText,
    title: 'The classic AP stack: Capture + WFR + Imaging + SOA + ADF',
    detail:
      'A typical higher-ed AP layer combines WebCenter Enterprise Capture for ingestion, WebCenter Forms Recognition for header and line extraction, WebCenter Imaging for storage and retrieval, SOA Suite composites for orchestration and EBS / Fusion posting, and an ADF customization for non-PO coding. Each component has its own lifecycle dependency on Fusion Middleware 12c.',
  },
  {
    icon: BookOpen,
    title: 'Decade-plus of accumulated customizations',
    detail:
      'WFR projects tuned over years against the institution\'s supplier base. SOA composites encoding fund coding rules, grant tagging rules, and approval hierarchies that mirror the actual delegation policy. ADF form customizations layered on the non-PO coding screen for the specific chart-of-accounts structure. Documented in the heads of long-tenured staff more than in formal documents.',
  },
];

const HIGHER_ED_REQUIREMENTS = [
  {
    icon: Coins,
    title: 'Fund accounting',
    detail:
      'Restricted vs unrestricted funds, with restricted funds further segmented by donor intent, grant agreement, and use-of-funds covenants. Every invoice line carries a fund code; the AP system must validate fund availability and restricted-use eligibility before posting. Fund accounting is the structural difference between higher-ed AP and commercial AP.',
  },
  {
    icon: Beaker,
    title: 'Grant tracking and sponsored research',
    detail:
      'Federal grants (NIH, NSF, DOE), state grants, and private foundation grants each carry their own allowable-cost rules, period-of-performance limits, and reporting requirements. Per-grant coding has to flow through AP into the grants management module (Oracle Grants Accounting on EBS, Oracle Project Portfolio Management on Fusion) and feed effort reporting and sponsored research reporting.',
  },
  {
    icon: FileSearch,
    title: 'Encumbrance management',
    detail:
      'Higher-ed budgets are operated on an encumbrance basis — POs encumber budget, AP invoices liquidate the encumbrance and post actuals. WebCenter integration with EBS Payables or Fusion must respect open encumbrances, partial liquidations, and year-end encumbrance roll-forward. Encumbrance integrity is audited.',
  },
  {
    icon: Building2,
    title: 'Multi-entity consolidation',
    detail:
      'A single university often consolidates the academic operation, a separately-incorporated foundation, auxiliary services (housing, dining, athletics, parking), and an academic medical center — each with its own ledger, controls, and reporting boundary. Inter-entity AP transactions, shared-service AP postings, and consolidated reporting are routine requirements.',
  },
  {
    icon: ScrollText,
    title: 'F&A (indirect cost) rate calculations',
    detail:
      'Facilities and Administrative rates negotiated with the federal cognizant agency (typically DHHS or ONR) determine indirect cost recovery on sponsored research. The AP-to-grants flow has to support indirect cost allocation, on-the-fly F&A calculations on grant-charged invoices, and the periodic F&A rate proposal data pull. Errors here are audit findings.',
  },
  {
    icon: GraduationCap,
    title: 'Sponsored research compliance',
    detail:
      'Uniform Guidance (2 CFR 200) governs allowable costs, cost transfers, and documentation requirements on federal awards. AP invoices on sponsored awards need supporting documentation captured and retained, cost-principles compliance built into coding rules, and audit-ready retrieval. Single Audit (formerly OMB A-133) coverage means every AP touch on a federal dollar is potentially auditable.',
  },
];

const ACADEMIC_CALENDAR = [
  {
    period: 'July - August',
    constraint: 'Academic year open',
    detail:
      'Pre-term faculty hiring, departmental setup, residence life ramp, and student registration drive AP volume spikes. Any AP system change has to be complete and bedded in before this window; mid-cycle disruption affects the entire institution.',
  },
  {
    period: 'September - May',
    constraint: 'Academic operations and grant cycle',
    detail:
      'Normal-load AP throughout the academic year. Grant award activity is heaviest in the fall (federal fiscal year alignment) and spring. Mid-year is the lowest-disruption window for UAT and cutover, but only when bracketed by stable system state at both ends.',
  },
  {
    period: 'May - June',
    constraint: 'Fiscal year close',
    detail:
      'Year-end close runs June 30 for most public universities and many privates. Encumbrance roll-forward, accrual posting, F&A reconciliation, grant period-of-performance reconciliation, and audit prep all hit at once. AP system changes during this window are unworkable.',
  },
  {
    period: 'June - July',
    constraint: 'External audit and Single Audit fieldwork',
    detail:
      'External auditors and Single Audit fieldwork land in summer. AP transaction retrievability, supporting documentation, and approval-trail traceability are exercised under audit pressure. A migration in flight during this window creates audit risk.',
  },
];

const SCENARIOS = [
  {
    title: 'Mid-EBS-to-Fusion migration with WebCenter AP complexity in the way',
    body:
      'Institution started the EBS-to-Fusion migration two years ago; the AP / WebCenter layer is the part that has not moved. WFR rules, SOA composites posting via the Payables Open Interface, and the ADF coding form all need re-targeting against Fusion (FBDI, OIC, the Fusion AP UI). The December 2026 12c cliff turns "we will get to it" into "we have to decide now."',
  },
  {
    title: 'Grant-funded research with per-grant tagging the ADF form barely handles',
    body:
      'R1 / R2 research institutions process thousands of grant-charged invoices per month, each requiring grant ID, task, expenditure type, F&A applicability, and cost-principles compliance tagging. The ADF non-PO coding form was extended years ago to support grant fields; small changes are development projects, and the form is the bottleneck for AP staff productivity on grant-heavy departments.',
  },
  {
    title: 'State-funded university with procurement transparency reporting',
    body:
      'State legislatures and state controllers require procurement transparency reporting on public funds — vendor payments above thresholds, expenditure by category, supplier-diversity metrics. The reporting requirements have grown over the years; the AP system has to support extraction, and the WebCenter retrieval path has to support the public-records-request workload that follows.',
  },
  {
    title: 'Decentralized procurement across colleges, centralized AP',
    body:
      'Procurement authority is delegated to individual colleges, schools, and major centers — each with their own departmental requisitioners, approval hierarchies, and category preferences. AP is centralized in the controller\'s office. Coding rules, approval routing, and exception handling have to honor the decentralized procurement structure while posting through a centralized AP function. WebCenter SOA composites encode this; modernization has to preserve it.',
  },
];

const INTEGRATIONS = [
  {
    name: 'Banner',
    detail:
      'Ellucian Banner Finance is the dominant higher-ed ERP. WebCenter AP integrations to Banner Finance run through Banner APIs or staging tables for AP invoice posting, fund and organization (FOAPAL) coding, and encumbrance liquidation. Many institutions run Banner Finance as the system of record and Oracle EBS or Fusion in a different domain.',
  },
  {
    name: 'PeopleSoft Campus Solutions + Financials',
    detail:
      'Large state systems and many private universities run PeopleSoft Campus Solutions for student/SIS with PeopleSoft Financials for the finance side. AP modernization in these environments routes invoice posting through PeopleSoft Payables; WebCenter Forms Recognition and approval routing sit in front. PeopleSoft has its own December 2026 horizon and a separate decision frame.',
  },
  {
    name: 'Workday Student + Workday Financials',
    detail:
      'Workday Student is the newer entrant; institutions adopting Workday Student typically also run Workday Financials. AP modernization in a Workday environment routes through Workday Supplier Accounts; the WebCenter AP layer either gets replaced or feeds Workday via integration. This is rarely a 1:1 forward migration.',
  },
  {
    name: 'Oracle EBS R12 or Fusion Cloud ERP',
    detail:
      'Many higher-ed institutions run Oracle EBS R12 or have moved to Fusion Cloud ERP for the finance and HR domains. WebCenter AP posts via Payables Open Interface (EBS) or FBDI / REST (Fusion). The EBS-to-Fusion migration is a parallel project at many institutions and shapes the AP modernization choice.',
  },
  {
    name: 'Procurement systems',
    detail:
      'Jaggaer, Coupa, and SciQuest cover most higher-ed strategic sourcing and procurement. AP modernization preserves the procurement-system-to-AP integration — PO data flow, supplier master sync, contract pricing, and three-way match boundary. The procurement system rarely changes during AP modernization; the integration shape does.',
  },
  {
    name: 'Travel and expense',
    detail:
      'Concur is the dominant T&E system in higher-ed; some institutions run Chrome River or in-house. T&E integrates with AP for non-employee reimbursements, travel cards, and grant-charged travel with sponsored-research compliance requirements. AP modernization keeps T&E in scope as an upstream feeder.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    eyebrow: 'Tier 1 · Higher-Ed AP Assessment',
    title: 'Fixed-fee read of your higher-ed AP layer',
    body:
      'Andrew reads your existing WebCenter AP implementation in the higher-ed context — fund coding rules, grant tagging logic, encumbrance handling, F&A treatment, Banner / PeopleSoft / Workday integration, procurement-system boundary. Output is a forward plan that names the academic-year-aware migration window. Plan is yours to keep regardless of which delivery partner you choose.',
  },
  {
    eyebrow: 'Tier 2 · Architecture Review',
    title: 'One-week read of WFR rules, SOA composites, and the coding form',
    body:
      'Deeper, one-week fixed-fee read of the running stack. WFR projects (templates, learned data, supplier tuning), SOA composites encoding fund / grant / encumbrance logic, ADF non-PO coding customizations, the procurement integration, the SIS-to-finance boundary. Output: structured inventory used as input to any forward decision — 14c upgrade, modernization, or a hybrid path.',
  },
  {
    eyebrow: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through your decision window',
    body:
      'Monthly retainer with Andrew, sized for the institution working through a 6-18 month decision with controller, CFO, procurement, IT, sponsored programs, and Oracle / Ellucian / Workday account teams. Guidance on architecture choices, fund-accounting tradeoffs, grant-compliance preservation, and academic-year sequencing.',
  },
  {
    eyebrow: 'Tier 4 · Forward Deployed Engineering',
    title: 'Founder-led delivery of a specific outcome',
    body:
      'Project-based, founder-led work on a defined deliverable: WFR rules migration, SOA composite re-platforming, ADF coding form replacement, Banner / PeopleSoft / Workday AP integration build, OCI architecture and security setup, file store to OCI Object Storage migration. Scoped to a clear outcome and an academic-calendar-aware timeline.',
  },
];

const FAQS = [
  {
    q: 'Does EZ Cloud integrate with Banner Finance?',
    a: 'Yes — EZ Cloud integrates with Ellucian Banner Finance as a routine higher-ed pattern. AP invoice posting runs against the FOAPAL (Fund-Organization-Account-Program-Activity-Location) chart of accounts structure, with encumbrance liquidation against Banner POs, vendor master sync, and 1099 reporting feed. Integration shape depends on whether Banner is the system of record for the finance domain or whether Oracle EBS / Fusion / PeopleSoft is — both patterns are supported. Andrew has built Banner-integrated WebCenter AP at multiple institutions.',
  },
  {
    q: 'How does grant tracking work in a higher-ed AP modernization?',
    a: 'Grant tracking is built into the AP entry point, not bolted on after posting. Per-invoice-line grant coding (grant ID, task / award / project, expenditure type, F&A applicability) is captured at AP entry, validated against the grants management module (Oracle Grants Accounting on EBS, Project Portfolio Management on Fusion, or the equivalent in Banner / PeopleSoft / Workday), and posted with full sponsored-research compliance metadata. Period-of-performance limits, allowable-cost rules, and cost-transfer documentation are enforced at coding time, not after the fact.',
  },
  {
    q: 'How is encumbrance management handled?',
    a: 'Encumbrance integrity is preserved end-to-end and remains audit-traceable from invoice to encumbrance to PO to budget. PO-matched invoices liquidate the open encumbrance for the matched quantity and amount, leaving a residual encumbrance for the unliquidated balance. Non-PO invoices do not touch encumbrances. Year-end encumbrance roll-forward is supported via the standard ERP encumbrance roll process; the AP modernization layer does not override or shortcut the ERP encumbrance accounting.',
  },
  {
    q: 'How does fund accounting flow through — restricted vs unrestricted?',
    a: 'Every invoice line carries a fund code, and the AP modernization layer validates fund existence and status before routing through the restricted-vs-unrestricted distinction. Restricted-fund invoices honor donor intent and grant agreement use-of-funds covenants. For restricted funds tied to sponsored awards, coding validation extends to allowable-cost rules under the award terms; for restricted funds tied to gift agreements, coding validation extends to donor-intent restrictions captured in advancement or development records. Posting respects the fund-by-fund net asset classification (with-donor-restriction vs without-donor-restriction under ASU 2016-14).',
  },
  {
    q: 'How does F&A / indirect cost rate calculation interact with AP?',
    a: 'F&A (Facilities and Administrative) cost recovery on sponsored research is calculated on the modified total direct cost base, and the AP layer flags applicability per invoice line for the grants module to compute. The MTDC base typically covers the direct-cost portion of grant-charged invoices, with exclusions for equipment, subaward amounts over the first $25,000, tuition, and certain other categories. Each grant-charged invoice line is flagged with its F&A applicability (subject vs excluded vs partial) so the indirect cost allocation engine in the grants module can compute correctly. The periodic F&A rate proposal data pull is supported via standard AP and grants reporting; the AP layer is not the source of truth for the rate calculation itself.',
  },
  {
    q: 'We are running an RFP — can EZ Cloud respond?',
    a: 'Yes — EZ Cloud responds to higher-ed AP automation RFPs and Andrew has carried higher-ed responses through to award. These procurements often run as state-procurement RFPs, system-wide RFPs across a state university system, or institutional RFPs through purchasing. The /conferences page lists where Andrew is presenting in the next 12 months and may be a useful pre-RFP meeting touchpoint; see also /about for the engagement model.',
  },
  {
    q: 'What is the migration window in an academic-year context?',
    a: 'The viable higher-ed AP system migration window is mid-year — October through March — with November to February the lowest-disruption sub-window. Year-end close (June) and the academic year open (August) are both excluded. A typical 12c decision-to-cutover timeline runs 9-15 months for a higher-ed institution; planning starts now for an October 2027 cutover. Sponsored research compliance also constrains the window — Single Audit fieldwork in summer cannot be in conflict with an in-flight migration.',
  },
  {
    q: 'How much does this cost?',
    a: 'Engagement pricing is established per situation on a Decision Call. Tier 1 and Tier 2 are fixed-fee, scoped on a Decision Call. Tier 3 is a monthly retainer sized to the institution. Tier 4 is project-based, scoped to the defined outcome. State-procurement RFP scenarios are responded to under the institution\'s standard procurement framework; cooperative purchasing vehicles (E&I Cooperative Services, OMNIA Partners, GSA) are routinely used.',
  },
];

const WebCenterHigherEducation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter for Higher Education — AP Modernization for Universities | EZ Cloud',
    description:
      'Oracle WebCenter AP modernization for universities and colleges. Fund accounting, grant tracking, integration with Banner / PeopleSoft / Workday Student. The December 2026 12c support cliff and how higher-ed AP teams plan the forward path without disrupting the academic-year cycle.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-higher-education',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Higher Education' },
      { '@type': 'Thing', name: 'Higher Education AP Automation' },
      { '@type': 'Thing', name: 'Fund Accounting' },
      { '@type': 'Thing', name: 'Grant Management' },
      { '@type': 'Thing', name: 'Oracle EBS Higher Education' },
      { '@type': 'Thing', name: 'Banner ERP Integration' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter for Higher Education — AP Modernization for Universities | EZ Cloud"
        description="Oracle WebCenter AP modernization for universities and colleges. Fund accounting, grant tracking, integration with Banner / PeopleSoft / Workday Student. The December 2026 12c support cliff and how higher-ed AP teams plan the forward path without disrupting the academic-year cycle."
        canonical="https://ezcloud.ai/solutions/webcenter-higher-education"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter for Higher Education',
            url: 'https://ezcloud.ai/solutions/webcenter-higher-education',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <GraduationCap className="w-4 h-4" aria-hidden />
              Higher Education · Oracle WebCenter AP
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter for higher education
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                AP modernization for universities and colleges. Fund accounting, grant tracking,
                Banner / PeopleSoft Campus / Workday Student integration. The December 2026
                decision in the academic-year context.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Higher-ed AP is not commercial AP. Fund accounting, encumbrances, F&A indirect cost
              rates, sponsored research compliance, and a migration window constrained by the
              academic calendar all shape the December 2026 decision in their own way.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-higher-ed-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Higher-Ed WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              WebCenter AP modernization for higher ed, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter AP at a university or college sits at the intersection of fund
                  accounting, sponsored research, encumbered budgets, and the SIS-to-finance
                  boundary.
                </strong>{' '}
                The same WebCenter Capture + Forms Recognition + Imaging + SOA Suite + ADF
                non-PO coding stack that runs commercial AP runs higher-ed AP — but the coding
                rules, approval routing, and compliance metadata are materially different.
              </p>
              <p>
                Oracle Fusion Middleware 12c Premier Support ends{' '}
                <strong className="text-gray-900">December 2026</strong>, with Extended Support
                ending December 2027. For higher-ed AP teams, the decision is shaped by the
                academic-year cycle: fiscal year close in June, academic year open in August,
                Single Audit fieldwork in summer. The viable migration window is narrow and has to
                be planned now.
              </p>
              <p>
                Forward paths range from a{' '}
                <Link
                  to="/solutions/webcenter-12c-end-of-support-decision"
                  className="text-accent font-semibold hover:underline"
                >
                  12c support decision framework
                </Link>{' '}
                to a full{' '}
                <Link
                  to="/solutions/modernize-oracle-webcenter"
                  className="text-accent font-semibold hover:underline"
                >
                  WebCenter modernization
                </Link>
                . The right answer depends on the ERP layer (Banner / PeopleSoft / Workday / Oracle
                EBS / Fusion), the grants posture, the institutional risk tolerance, and the
                academic-calendar window available.
              </p>
            </div>
          </div>
        </section>

        {/* Where higher-ed WebCenter customers are today */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Where higher-ed WebCenter customers are today
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The typical higher-ed WebCenter AP stack
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Most higher-ed WebCenter AP implementations share four characteristics. Knowing the
              shape going in makes the December 2026 decision conversation faster.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {TODAY_STATE.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Higher-ed AP-specific requirements */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Higher-ed AP-specific requirements
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Six requirements that distinguish higher-ed AP from commercial AP
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Any forward path — 14c upgrade or modernization — has to preserve these capabilities.
              Each is its own design conversation; together they define the shape of higher-ed AP.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {HIGHER_ED_REQUIREMENTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              See also our{' '}
              <Link to="/industries/education" className="text-accent font-semibold hover:underline">
                Education industry overview
              </Link>{' '}
              for the broader AP automation framing across K-12 and higher ed.
            </p>
          </div>
        </section>

        {/* The December 2026 decision in the academic-year context */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The December 2026 decision in the academic-year context
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The migration window is constrained — plan now
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Universities cannot disrupt the June 30 fiscal close or the August academic-year
              open. The viable AP system change window is mid-year, and it has to be bracketed by
              stable system state at both ends.
            </p>
            <div className="space-y-5">
              {ACADEMIC_CALENDAR.map((p) => (
                <Card key={p.period} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div className="flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide bg-accent/10 text-accent border border-accent/30">
                      <CalendarRange className="w-4 h-4 inline mr-2" aria-hidden />
                      {p.period}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{p.constraint}</h3>
                      <p className="text-gray-700 leading-relaxed">{p.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              For a forward path that respects the calendar, see the{' '}
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="text-accent font-semibold hover:underline"
              >
                12c end-of-support decision framework
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Integration considerations */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Integration considerations
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The systems your AP layer talks to
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Higher-ed AP modernization is shaped by the surrounding ERP, SIS, procurement, and
              T&E stack. The integration map matters more than the AP product choice on its own.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {INTEGRATIONS.map((i) => (
                <Card key={i.name} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-gray-900 font-semibold mb-2">{i.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{i.detail}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              For Oracle EBS-on-education specifics, see{' '}
              <Link
                to="/solutions/education-oracle-ebs"
                className="text-accent font-semibold hover:underline"
              >
                Education on Oracle EBS
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Common scenarios we hear */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four scenarios that show up on Decision Calls
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The conversations Andrew is having with higher-ed AP teams in 2026 cluster around
              these four shapes. Yours likely overlaps one of them.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map((s) => (
                <Card key={s.title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">{s.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* RFP posture */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              RFP posture
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
              Higher-ed procurements often run as RFPs
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Higher-ed AP automation buys are frequently structured as RFPs — state-procurement
                RFPs at public universities, system-wide RFPs across state university systems, or
                institutional RFPs through the purchasing office at privates. Cooperative
                purchasing vehicles (E&I Cooperative Services, OMNIA Partners, GSA) are routinely
                used.
              </p>
              <p>
                EZ Cloud responds to higher-ed RFPs. Andrew has carried higher-ed AP responses
                through to award. If your institution is in pre-RFP, RFP, or post-shortlist
                evaluation phase, a Decision Call is the right starting point.
              </p>
              <p>
                A pre-RFP meeting at one of the conferences Andrew is presenting at can shorten the
                evaluation cycle. See the{' '}
                <Link to="/conferences" className="text-accent font-semibold hover:underline">
                  conferences page
                </Link>{' '}
                for upcoming events. The{' '}
                <Link to="/about" className="text-accent font-semibold hover:underline">
                  About page
                </Link>{' '}
                covers the engagement model, founder-led delivery posture, and Oracle relationship.
              </p>
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages with higher-ed institutions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for higher-ed WebCenter AP
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level WebCenter expertise applies to higher-ed AP — with
              fund accounting, grants, encumbrance, and academic-calendar sequencing as
              first-class inputs to every tier.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.eyebrow} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.eyebrow}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, with stack-specific considerations,
              eight diagnostic questions, and a vendor evaluation checklist. Useful pre-read for
              higher-ed AP teams entering the 12c decision window.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on higher-ed WebCenter AP
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your higher-ed WebCenter AP decision
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. Fund accounting, grant tracking, encumbrance, F&A,
              Banner / PeopleSoft / Workday integration, and the academic-calendar sequencing
              window — walked through against your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-higher-ed-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Higher-Ed WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterHigherEducation;
